// src/Components/ImportMarkers/ExcelDataView.js

import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { useDuplicateCheckOnUpload } from './DuplicateCheckOnUpload';

const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };

const normalizeString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

const getIconUrl = (iconObj) => {
  if (!iconObj) return null;
  return (
    iconObj.url ||
    iconObj.imageUrl ||
    iconObj.iconUrl ||
    iconObj.downloadURL ||
    iconObj.image ||
    iconObj.src ||
    iconObj.fileUrl ||
    (typeof iconObj.icon === 'string' ? iconObj.icon : null)
  );
};

// AMENDED: Standard properties list to protect from deletion natively
const STANDARD_PROPERTIES_NORMALIZED = [
  'title',
  'category',
  'region',
  'address',
  'city',
  'state',
  'zip',
  'postcode/zip',
  'country',
  'clientname',
  'controller',
  'keywords',
  'website',
  'date',
  'showcase',
  'hilight',
  'visibility',
  'hovertext',
  'description',
  'photos',
  'hoverphoto',
  'icon',
  'locationid',
  'documents',
  'position(lat,lng)',
  'position',
  'lat',
  'lng',
  'latitude',
  'longitude',
];

export default function ExcelDataView({
  hasData,
  parsedData,
  columns,
  successfullyImportedIds,
  selectedRowIds,
  handleSelectAllRows,
  handleSelectRow,
  rowMappings,
  rowSubMappings,
  customRows,
  regions,
  categories,
  markerIcons,
  setInvalidMatchState,
  setManualMatchRowId,
  setResolveCoordsState,
  makeUppercase,
  activeRowId,
  pendingNextColumn,
  setPendingNextColumn,
  invalidMatchState,
  resolveCoordsState,
  manualMatchRowId,
  pendingReinstateBatchIds,
  setDocumentMatchRowId,
  documentMatchRowId,
  handleDeleteColumnClick,
  localMenus, // AMENDED: Receiving local menus to scan historical mappings
}) {
  // --- AUTO-SCROLL TO ACTIVE ROW ---
  useEffect(() => {
    if (activeRowId) {
      const el = document.getElementById(`excel-row-${activeRowId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeRowId]);

  const duplicateMap = useDuplicateCheckOnUpload({
    parsedData,
    successfullyImportedIds,
    rowMappings,
    customRows,
    pendingReinstateBatchIds,
  });

  // --- 1. RESOLVE ASSIGNED TEMPLATE PROPERTIES ---
  const getColName = (key) => {
    if (!key) return null;
    const cRow = customRows?.find((r) => r.id === key);
    if (cRow && cRow.name && cRow.name !== '-') return cRow.name;
    if (key.startsWith('prop_')) return key.substring(5);
    return key;
  };

  const regionColName = getColName(
    Object.keys(rowMappings).find(
      (k) => rowMappings[k] === 'INFO' && rowSubMappings[k] === 'REGION',
    ),
  );

  const categoryColName = getColName(
    Object.keys(rowMappings).find(
      (k) => rowMappings[k] === 'INFO' && rowSubMappings[k] === 'CATEGORY',
    ),
  );

  const titleColName = getColName(
    Object.keys(rowMappings).find(
      (k) => rowMappings[k] === 'INFO' && rowSubMappings[k] === 'TITLE',
    ),
  );

  const templatePhotoColKey = Object.keys(rowMappings).find(
    (k) =>
      normalizeString(rowMappings[k]) === 'photos' ||
      normalizeString(rowMappings[k]) === 'photo' ||
      normalizeString(rowSubMappings[k]) === 'photos' ||
      normalizeString(rowSubMappings[k]) === 'photo',
  );
  const templatePhotoColName = getColName(templatePhotoColKey);

  const templateDocumentColKey = Object.keys(rowMappings).find(
    (k) => rowMappings[k] === 'DOCUMENTS',
  );
  const templateDocumentColName = getColName(templateDocumentColKey);

  // AMENDED: Check for both standard and historical POSITION mappings
  const positionColKey = Object.keys(rowMappings).find(
    (k) => rowMappings[k] === 'POSITION (LAT, LNG)' || rowMappings[k] === 'POSITION',
  );
  let positionColName = getColName(positionColKey);

  // AMENDED: If there is no combined POSITION column, hijack the LAT column to show the map icon
  if (!positionColName) {
    const latColKey = Object.keys(rowMappings).find(
      (k) =>
        rowMappings[k] === 'LATITUDE' ||
        rowMappings[k] === 'LAT' ||
        rowSubMappings[k] === 'LATITUDE' ||
        rowSubMappings[k] === 'LAT',
    );
    if (latColKey) positionColName = getColName(latColKey);
  }

  const descriptionColName = getColName(
    Object.keys(rowMappings).find((k) => rowMappings[k] === 'DESCRIPTION'),
  );

  const iconColKey = Object.keys(rowMappings).find((k) => rowMappings[k] === 'ICON');
  const iconColName = getColName(iconColKey);
  const hasGlobalIcon = iconColKey && rowSubMappings[iconColKey];

  const showcaseColName = getColName(
    Object.keys(rowMappings).find(
      (k) => rowMappings[k] === 'INFO' && rowSubMappings[k] === 'SHOWCASE',
    ),
  );

  const hilightColName = getColName(
    Object.keys(rowMappings).find(
      (k) => rowMappings[k] === 'INFO' && rowSubMappings[k] === 'HILIGHT',
    ),
  );

  // --- MULTI-COLUMN GEOCODING HELPER ---
  const getInfoColName = (subTarget) => {
    const key = Object.keys(rowMappings).find(
      (k) => rowMappings[k] === 'INFO' && rowSubMappings[k] === subTarget,
    );
    return getColName(key);
  };

  const getSearchString = (r) => {
    const parts = [];
    ['TITLE', 'LOCATION ADDRESS', 'CITY', 'STATE', 'POSTCODE/ZIP', 'COUNTRY'].forEach(
      (field) => {
        const col = getInfoColName(field);
        if (col) {
          const actualKey = Object.keys(r).find(
            (k) => normalizeString(k) === normalizeString(col),
          );
          if (actualKey && r[actualKey]) parts.push(String(r[actualKey]).trim());
        }
      },
    );
    if (parts.length === 0) {
      let fallback = null;
      if (titleColName) {
        const tKey = Object.keys(r).find(
          (k) => normalizeString(k) === normalizeString(titleColName),
        );
        fallback = tKey ? r[tKey] : null;
      }
      if (!fallback)
        fallback = r['Title'] || r['title'] || (columns.length > 0 ? r[columns[0]] : '');
      if (fallback) parts.push(String(fallback).trim());
    }
    return parts.filter(Boolean).join(', ');
  };

  const getGeoSuffix = (r) => {
    const parts = [];
    ['LOCATION ADDRESS', 'CITY', 'STATE', 'POSTCODE/ZIP', 'COUNTRY'].forEach((field) => {
      const col = getInfoColName(field);
      if (col) {
        const actualKey = Object.keys(r).find(
          (k) => normalizeString(k) === normalizeString(col),
        );
        if (actualKey && r[actualKey]) parts.push(String(r[actualKey]).trim());
      }
    });
    return parts.filter(Boolean).join(', ');
  };

  // --- 2. COMPUTE DYNAMIC GRID COLUMNS SEGREGATION ZONES ---
  const activeTemplateProperties = customRows
    .filter((r) => rowMappings[r.id] && r.name !== '-')
    .map((r) => r.name);

  const incomingExcelHeadersNormalized = columns.map((c) => normalizeString(c));

  // Deduplicate available columns preserving initial array order
  const availableCols = [];
  const seenNormalized = new Set();

  columns.forEach((col) => {
    const norm = normalizeString(col);
    if (!seenNormalized.has(norm)) {
      seenNormalized.add(norm);
      availableCols.push(col);
    }
  });

  // Extract mapped columns strictly in Page 4 (customRows) ordering
  const mappedColsInOrder = [];
  const seenMappedNorms = new Set();

  if (customRows && Array.isArray(customRows)) {
    customRows.forEach((r) => {
      if (r.id && rowMappings[r.id] && r.name && r.name !== '-') {
        const norm = normalizeString(r.name);
        const matchedCol =
          availableCols.find((c) => normalizeString(c) === norm) || r.name;
        const matchedNorm = normalizeString(matchedCol);
        if (!seenMappedNorms.has(matchedNorm)) {
          seenMappedNorms.add(matchedNorm);
          mappedColsInOrder.push(matchedCol);
        }
      }
    });
  }

  // Identify Title column if mapped
  let actualTitleCol = null;
  if (titleColName) {
    actualTitleCol =
      availableCols.find((c) => normalizeString(c) === normalizeString(titleColName)) ||
      titleColName;
  }

  // AMENDED: Build a set of all historically mapped properties from all templates
  const historicallyMappedNorms = new Set();
  if (localMenus && Array.isArray(localMenus)) {
    localMenus.forEach((menu) => {
      if (menu.mappings && Array.isArray(menu.mappings)) {
        menu.mappings.forEach((m) => {
          if (m.property && m.property !== '-') {
            historicallyMappedNorms.add(normalizeString(m.property));
          }
        });
      } else if (menu.customRows && Array.isArray(menu.customRows)) {
        menu.customRows.forEach((cr) => {
          if (cr.name && cr.name !== '-') {
            historicallyMappedNorms.add(normalizeString(cr.name));
          }
        });
      }
    });
  }

  // Mapped properties come before unmapped properties
  const unmappedCols = availableCols.filter(
    (c) => !seenMappedNorms.has(normalizeString(c)),
  );

  // AMENDED: Segregate unmapped columns so that deletable (Xcross) properties always appear last
  const protectedUnmappedCols = [];
  const deletableUnmappedCols = [];

  unmappedCols.forEach((col) => {
    const normCol = normalizeString(col);
    const isPage4Property = customRows.some(
      (cr) => normalizeString(cr.name) === normCol && cr.name !== '-',
    );
    const isStandardProperty = STANDARD_PROPERTIES_NORMALIZED.includes(normCol);
    const isHistoricallyMapped = historicallyMappedNorms.has(normCol);

    if (isPage4Property || isStandardProperty || isHistoricallyMapped) {
      protectedUnmappedCols.push(col);
    } else {
      deletableUnmappedCols.push(col);
    }
  });

  const dynamicGridColumns = [
    ...mappedColsInOrder,
    ...protectedUnmappedCols,
    ...deletableUnmappedCols,
  ];

  // --- SIMULATED CLICK FOR AUTO NEXT COLUMN NAVIGATION ---
  useEffect(() => {
    if (pendingNextColumn) {
      const { rowId, colName, direction, rowDirection } = pendingNextColumn;
      setPendingNextColumn(null);

      const currentIdx = dynamicGridColumns.findIndex(
        (c) => normalizeString(c) === normalizeString(colName),
      );

      let targetCol = null;
      let targetRowId = rowId;

      if (rowDirection) {
        targetCol = colName;
        const currentRowIdx = parsedData.findIndex((r) => r.id === rowId);
        if (currentRowIdx !== -1 && parsedData.length > 0) {
          if (rowDirection === 'up') {
            targetRowId =
              currentRowIdx > 0
                ? parsedData[currentRowIdx - 1].id
                : parsedData[parsedData.length - 1].id;
          } else if (rowDirection === 'down') {
            targetRowId =
              currentRowIdx < parsedData.length - 1
                ? parsedData[currentRowIdx + 1].id
                : parsedData[0].id;
          }
        }
      } else if (currentIdx !== -1 && dynamicGridColumns.length > 0) {
        if (direction === 'prev') {
          targetCol =
            currentIdx > 0
              ? dynamicGridColumns[currentIdx - 1]
              : dynamicGridColumns[dynamicGridColumns.length - 1];
        } else {
          targetCol =
            currentIdx < dynamicGridColumns.length - 1
              ? dynamicGridColumns[currentIdx + 1]
              : dynamicGridColumns[0];
        }
      }

      if (targetCol) {
        const row = parsedData.find((r) => r.id === targetRowId);
        if (row) {
          const actualRowKey = Object.keys(row).find(
            (k) => normalizeString(k) === normalizeString(targetCol),
          );
          const cellValue = actualRowKey ? row[actualRowKey] : undefined;

          const isTitleCol = targetCol === actualTitleCol;
          const isPositionCol =
            positionColName &&
            normalizeString(targetCol) === normalizeString(positionColName);
          const isRegionCol =
            regionColName &&
            normalizeString(targetCol) === normalizeString(regionColName);
          const isCategoryCol =
            categoryColName &&
            normalizeString(targetCol) === normalizeString(categoryColName);
          const isIconCol =
            iconColName && normalizeString(targetCol) === normalizeString(iconColName);
          const isDescriptionCol =
            descriptionColName &&
            normalizeString(targetCol) === normalizeString(descriptionColName);

          const isPhotoColumn =
            templatePhotoColName &&
            normalizeString(targetCol) === normalizeString(templatePhotoColName);

          const isDocumentCol =
            templateDocumentColName &&
            normalizeString(targetCol) === normalizeString(templateDocumentColName);

          const isTemplateProperty =
            activeTemplateProperties.some(
              (p) => normalizeString(p) === normalizeString(targetCol),
            ) ||
            isPhotoColumn ||
            isDocumentCol;

          if (isTitleCol) {
            setManualMatchRowId(null);
            if (setDocumentMatchRowId) setDocumentMatchRowId(null);
            setResolveCoordsState({
              open: false,
              rowId: null,
              searchTarget: '',
              actualTitle: '',
              geoSuffix: '',
            });
            setInvalidMatchState({
              open: true,
              rowId: row.id,
              colName: targetCol,
              cellValue: cellValue ?? '',
              type: 'TEMPLATE_MISMATCH',
              searchTarget: getSearchString(row),
            });
          } else if (isPositionCol) {
            const actualTitleRowKey = actualTitleCol
              ? Object.keys(row).find(
                  (k) => normalizeString(k) === normalizeString(actualTitleCol),
                )
              : null;
            const titleVal = actualTitleRowKey ? row[actualTitleRowKey] : '';
            const fallbackTitle =
              titleVal ||
              row['Title'] ||
              row['TITLE'] ||
              row['title'] ||
              (columns.length > 0 ? row[columns[0]] : '');

            setManualMatchRowId(null);
            if (setDocumentMatchRowId) setDocumentMatchRowId(null);
            setInvalidMatchState({
              open: false,
              rowId: null,
              colName: null,
              cellValue: '',
              type: null,
              searchTarget: '',
            });
            setResolveCoordsState({
              open: true,
              rowId: row.id,
              searchTarget: fallbackTitle,
              actualTitle: fallbackTitle,
              geoSuffix: getGeoSuffix(row),
            });
          } else if (isTemplateProperty) {
            if (isPhotoColumn) {
              setInvalidMatchState({
                open: false,
                rowId: null,
                colName: null,
                cellValue: '',
                type: null,
                searchTarget: '',
              });
              setResolveCoordsState({
                open: false,
                rowId: null,
                searchTarget: '',
                actualTitle: '',
                geoSuffix: '',
              });
              if (setDocumentMatchRowId) setDocumentMatchRowId(null);
              setManualMatchRowId(row.id);
            } else if (isDocumentCol) {
              setInvalidMatchState({
                open: false,
                rowId: null,
                colName: null,
                cellValue: '',
                type: null,
                searchTarget: '',
              });
              setResolveCoordsState({
                open: false,
                rowId: null,
                searchTarget: '',
                actualTitle: '',
                geoSuffix: '',
              });
              setManualMatchRowId(null);
              if (setDocumentMatchRowId) setDocumentMatchRowId(row.id);
            } else if (isIconCol) {
              setManualMatchRowId(null);
              if (setDocumentMatchRowId) setDocumentMatchRowId(null);
              setResolveCoordsState({
                open: false,
                rowId: null,
                searchTarget: '',
                actualTitle: '',
                geoSuffix: '',
              });
              setInvalidMatchState({
                open: true,
                rowId: row.id,
                colName: targetCol,
                cellValue: cellValue ?? '',
                type: 'ICON',
                searchTarget: getSearchString(row),
              });
            } else {
              setManualMatchRowId(null);
              if (setDocumentMatchRowId) setDocumentMatchRowId(null);
              setResolveCoordsState({
                open: false,
                rowId: null,
                searchTarget: '',
                actualTitle: '',
                geoSuffix: '',
              });
              setInvalidMatchState({
                open: true,
                rowId: row.id,
                colName: targetCol,
                cellValue: cellValue ?? '',
                type: isRegionCol
                  ? 'REGION'
                  : isCategoryCol
                    ? 'CATEGORY'
                    : isDescriptionCol
                      ? 'DESCRIPTION'
                      : 'TEMPLATE_MISMATCH',
                searchTarget: getSearchString(row),
              });
            }
          } else {
            setManualMatchRowId(null);
            if (setDocumentMatchRowId) setDocumentMatchRowId(null);
            setResolveCoordsState({
              open: false,
              rowId: null,
              searchTarget: '',
              actualTitle: '',
              geoSuffix: '',
            });
            setInvalidMatchState({
              open: true,
              rowId: row.id,
              colName: targetCol,
              cellValue: cellValue ?? '',
              type: 'TEXT',
              searchTarget: getSearchString(row),
            });
          }
        }
      } else {
        setInvalidMatchState({
          open: false,
          rowId: null,
          colName: null,
          cellValue: '',
          type: null,
          searchTarget: '',
        });
        setResolveCoordsState({
          open: false,
          rowId: null,
          searchTarget: '',
          actualTitle: '',
          geoSuffix: '',
        });
        setManualMatchRowId(null);
        if (setDocumentMatchRowId) setDocumentMatchRowId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNextColumn, dynamicGridColumns, parsedData]);

  // --- CUSTOM EVENT LISTENERS FOR DECOUPLED NEXT NAVIGATION ---
  useEffect(() => {
    const handleTriggerNext = (e) => {
      if (e.detail && setPendingNextColumn) {
        setPendingNextColumn(e.detail);
      }
    };

    const handleClosePhotos = () => {
      if (setManualMatchRowId) {
        setManualMatchRowId(null);
      }
      if (setDocumentMatchRowId) {
        setDocumentMatchRowId(null);
      }
    };

    window.addEventListener('loktr-trigger-next-column', handleTriggerNext);
    window.addEventListener('loktr-close-photos-panel', handleClosePhotos);

    return () => {
      window.removeEventListener('loktr-trigger-next-column', handleTriggerNext);
      window.removeEventListener('loktr-close-photos-panel', handleClosePhotos);
    };
  }, [setPendingNextColumn, setManualMatchRowId, setDocumentMatchRowId]);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flexGrow: 1,
        width: '100%',
        boxSizing: 'border-box',
        borderTop: hasData ? '2px solid #1976d2' : 'none',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
        overflowY: 'auto',
        overflowX: 'auto',
      }}>
      {!hasData ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 3,
            textAlign: 'center',
            gap: 2,
          }}>
          <Typography color='text.secondary' variant='body2' sx={{ fontStyle: 'italic' }}>
            Click the Upload button to select an Excel file to import.
          </Typography>
        </Box>
      ) : (
        <Table stickyHeader size='small' sx={{ minWidth: 650, tableLayout: 'auto' }}>
          <TableHead>
            <TableRow
              sx={{
                height: '56px',
                '& th': {
                  backgroundColor: '#e3f2fd',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #1976d2',
                  py: '4px',
                  ...cellStyle,
                },
              }}>
              <TableCell
                padding='checkbox'
                sx={{
                  width: '64px',
                  minWidth: '64px',
                  maxWidth: '64px',
                  textAlign: 'center',
                  verticalAlign: 'bottom',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'flex-end',
                    pb: '2px',
                    alignItems: 'center',
                  }}>
                  <Checkbox
                    size='small'
                    color='primary'
                    checked={
                      parsedData.length > 0 &&
                      parsedData.filter((r) => !successfullyImportedIds.includes(r.id))
                        .length > 0 &&
                      selectedRowIds.length ===
                        parsedData.filter((r) => !successfullyImportedIds.includes(r.id))
                          .length
                    }
                    indeterminate={
                      selectedRowIds.length > 0 &&
                      selectedRowIds.length <
                        parsedData.filter((r) => !successfullyImportedIds.includes(r.id))
                          .length
                    }
                    onChange={handleSelectAllRows}
                    sx={{ p: 0.5, '&.Mui-checked': { color: '#1976d2' } }}
                  />
                </Box>
              </TableCell>
              {dynamicGridColumns.map((col, index) => {
                const isTitleCol = actualTitleCol && col === actualTitleCol;
                const displayCol =
                  makeUppercase && typeof col === 'string' ? col.toUpperCase() : col;

                const normCol = normalizeString(col);
                const isMapped = seenMappedNorms.has(normCol);
                const isPage4Property = customRows.some(
                  (cr) => normalizeString(cr.name) === normCol && cr.name !== '-',
                );

                // AMENDED: Protect standard properties AND historically mapped properties from deletion
                const isStandardProperty =
                  STANDARD_PROPERTIES_NORMALIZED.includes(normCol);
                const isHistoricallyMapped = historicallyMappedNorms.has(normCol);

                const isDeletable =
                  !isMapped &&
                  !isPage4Property &&
                  !isStandardProperty &&
                  !isHistoricallyMapped;

                return (
                  <TableCell
                    key={index}
                    align={isTitleCol || index === 0 ? 'left' : 'center'}
                    sx={{ whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isTitleCol || index === 0 ? 'flex-start' : 'center',
                        justifyContent: 'flex-end',
                        height: '100%',
                        gap: 0.5,
                      }}>
                      {isDeletable ? (
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation();
                            if (handleDeleteColumnClick) handleDeleteColumnClick(col);
                          }}
                          sx={{
                            p: 0,
                            color: 'black',
                            backgroundColor: '#ffffff',
                            border: '1px solid black',
                            width: 16,
                            height: 16,
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                              color: '#d32f2f',
                            },
                          }}
                          title='Delete Property'>
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      ) : (
                        <Box sx={{ height: 16, width: 16 }} />
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent:
                            isTitleCol || index === 0 ? 'flex-start' : 'center',
                        }}>
                        {templatePhotoColName &&
                        normalizeString(col) === normalizeString(templatePhotoColName) ? (
                          <Tooltip
                            title={
                              <React.Fragment>
                                Capitalisation, spaces, and commas are ignored.
                                <br />
                                Use -1, _1, or (1) for multiple photos.
                              </React.Fragment>
                            }
                            arrow
                            placement='top'
                            slotProps={{ popper: { sx: { zIndex: 100005 } } }}>
                            <Box
                              component='span'
                              sx={{ cursor: 'help', borderBottom: '1px dotted #1976d2' }}>
                              {displayCol}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Box component='span'>{displayCol}</Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {parsedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={dynamicGridColumns.length + 1}
                  align='center'
                  sx={{ py: 4 }}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontStyle: 'italic' }}>
                    No data rows detected. Go to the next step (Settings View) to
                    configure your mapping relationships for these columns.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              parsedData.map((row) => {
                const isImported = successfullyImportedIds.includes(row.id);
                const isSelected = selectedRowIds.includes(row.id) && !isImported;
                const isActiveRow = activeRowId === row.id;

                const actualTitleRowKey = actualTitleCol
                  ? Object.keys(row).find(
                      (k) => normalizeString(k) === normalizeString(actualTitleCol),
                    )
                  : null;
                const titleVal = actualTitleRowKey ? row[actualTitleRowKey] : '';
                const fallbackTitle =
                  titleVal ||
                  row['Title'] ||
                  row['TITLE'] ||
                  row['title'] ||
                  (columns.length > 0 ? row[columns[0]] : '');

                return (
                  <TableRow
                    key={row.id}
                    id={`excel-row-${row.id}`}
                    hover={!isImported}
                    selected={isSelected}
                    sx={{
                      height: '28px',
                      cursor: isImported ? 'default' : 'inherit',
                      bgcolor: isActiveRow
                        ? 'rgba(0, 0, 0, 0.08) !important'
                        : isImported
                          ? '#f5f5f5'
                          : 'inherit',
                      opacity: isImported ? 0.6 : 1,
                      '& td': { py: '2px', ...cellStyle, whiteSpace: 'nowrap' },
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&.Mui-selected': { backgroundColor: 'transparent !important' },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                      },
                    }}>
                    <TableCell
                      padding='checkbox'
                      sx={{
                        width: '64px',
                        minWidth: '64px',
                        maxWidth: '64px',
                        textAlign: 'center',
                      }}>
                      <Checkbox
                        checked={isSelected || isImported}
                        disabled={isImported}
                        size='small'
                        onChange={() => !isImported && handleSelectRow(row.id)}
                        sx={{
                          p: 0.5,
                          '&.Mui-checked': { color: isImported ? '#9e9e9e' : '#1976d2' },
                        }}
                      />
                    </TableCell>
                    {dynamicGridColumns.map((col, colIndex) => {
                      const actualRowKey = Object.keys(row).find(
                        (k) => normalizeString(k) === normalizeString(col),
                      );
                      const cellValue = actualRowKey ? row[actualRowKey] : undefined;

                      const isDescriptionCol =
                        descriptionColName &&
                        normalizeString(col) === normalizeString(descriptionColName);

                      let cellHasData =
                        cellValue !== undefined &&
                        cellValue !== null &&
                        String(cellValue).trim() !== '' &&
                        String(cellValue).trim() !== '<p></p>';

                      if (isDescriptionCol && cellHasData) {
                        const plainText = String(cellValue)
                          .replace(/<[^>]*>?/gm, '')
                          .replace(/&nbsp;/g, '')
                          .trim();
                        if (plainText === '') {
                          cellHasData = false;
                        }
                      }

                      const isNamedRichText =
                        normalizeString(col) === normalizeString('RICH TEXT');

                      const isPhotoColumn =
                        templatePhotoColName &&
                        normalizeString(col) === normalizeString(templatePhotoColName);

                      const isDocumentCol =
                        templateDocumentColName &&
                        normalizeString(col) === normalizeString(templateDocumentColName);

                      const isTitleCol = actualTitleCol && col === actualTitleCol;

                      const isPositionCol =
                        positionColName &&
                        normalizeString(col) === normalizeString(positionColName);

                      const isRegionCol =
                        regionColName &&
                        normalizeString(col) === normalizeString(regionColName);
                      const isCategoryCol =
                        categoryColName &&
                        normalizeString(col) === normalizeString(categoryColName);
                      const isIconCol =
                        iconColName &&
                        normalizeString(col) === normalizeString(iconColName);

                      const isShowcaseCol =
                        showcaseColName &&
                        normalizeString(col) === normalizeString(showcaseColName);
                      const isHilightCol =
                        hilightColName &&
                        normalizeString(col) === normalizeString(hilightColName);
                      const isBooleanCol = isShowcaseCol || isHilightCol;

                      const isTemplateProperty =
                        activeTemplateProperties.some(
                          (p) => normalizeString(p) === normalizeString(col),
                        ) ||
                        isPhotoColumn ||
                        isDocumentCol;

                      const isEffectiveTemplateProperty =
                        isTemplateProperty ||
                        isTitleCol ||
                        isPositionCol ||
                        isRegionCol ||
                        isCategoryCol ||
                        isIconCol ||
                        isDescriptionCol ||
                        isBooleanCol ||
                        isPhotoColumn ||
                        isDocumentCol;

                      const headerExistsInExcel = incomingExcelHeadersNormalized.includes(
                        normalizeString(col),
                      );

                      const manualKey = Object.keys(row._manuallyAssigned || {}).find(
                        (k) => normalizeString(k) === normalizeString(col),
                      );
                      const isManuallyAssigned = !!(
                        manualKey && row._manuallyAssigned[manualKey]
                      );

                      let isInvalidMatch = false;

                      if (isEffectiveTemplateProperty && !isPositionCol) {
                        if (isPhotoColumn) {
                          if (row.photoMatch !== 'MATCH') isInvalidMatch = true;
                        } else if (isDocumentCol) {
                          if (!row.documents || row.documents.length === 0)
                            isInvalidMatch = true;
                        } else if (isIconCol) {
                          isInvalidMatch = false;
                        } else if (isNamedRichText) {
                          isInvalidMatch = false;
                        } else if (isBooleanCol) {
                          if (!cellHasData && !isManuallyAssigned) {
                            isInvalidMatch = true;
                          } else {
                            isInvalidMatch = false;
                          }
                        } else if (isManuallyAssigned) {
                          if (!cellHasData) isInvalidMatch = true;
                          else isInvalidMatch = false;
                        } else if (isDescriptionCol) {
                          if (!cellHasData) isInvalidMatch = true;
                        } else if (isTitleCol) {
                          if (!cellHasData) isInvalidMatch = true;
                        } else if (headerExistsInExcel && cellHasData) {
                          if (isRegionCol) {
                            const isMatch = regions.some(
                              (r) =>
                                r.name &&
                                normalizeString(r.name) === normalizeString(cellValue),
                            );
                            if (!isMatch) isInvalidMatch = true;
                          } else if (isCategoryCol) {
                            const isMatch = categories.some(
                              (c) =>
                                c.name &&
                                normalizeString(c.name) === normalizeString(cellValue),
                            );
                            if (!isMatch) isInvalidMatch = true;
                          }
                        } else {
                          isInvalidMatch = true;
                        }
                      }

                      const isCellLockedIn =
                        isEffectiveTemplateProperty &&
                        !isManuallyAssigned &&
                        ((isIconCol && hasGlobalIcon) ||
                          (isPhotoColumn && row.photoMatch === 'MATCH') ||
                          (isDocumentCol && row.documents && row.documents.length > 0) ||
                          isNamedRichText ||
                          (isBooleanCol && cellHasData && !isInvalidMatch) ||
                          (!isIconCol &&
                            !isPhotoColumn &&
                            !isDocumentCol &&
                            !isNamedRichText &&
                            !isBooleanCol &&
                            headerExistsInExcel &&
                            cellHasData &&
                            !isInvalidMatch));

                      const isActiveCell =
                        isActiveRow &&
                        ((invalidMatchState?.open &&
                          normalizeString(invalidMatchState?.colName) ===
                            normalizeString(col)) ||
                          (resolveCoordsState?.open && isPositionCol) ||
                          (manualMatchRowId && isPhotoColumn) ||
                          (documentMatchRowId && isDocumentCol));

                      const isDuplicate = duplicateMap[row.id] || false;

                      return (
                        <TableCell
                          key={colIndex}
                          align={isTitleCol || colIndex === 0 ? 'left' : 'center'}
                          sx={{
                            boxShadow: isActiveCell ? 'inset 0 0 0 2px #1976d2' : 'none',
                          }}>
                          {isTitleCol ? (
                            /* OVERRIDE: TITLE COLUMN SHOWS TEXT ONLY, CLICKABLE TO EDIT */
                            <Tooltip
                              title={!isImported ? 'Click to edit Title' : ''}
                              arrow
                              placement='top'
                              slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                              <Box
                                onClick={(e) => {
                                  if (isImported) return;
                                  e.stopPropagation();
                                  setResolveCoordsState({
                                    open: false,
                                    rowId: null,
                                    searchTarget: '',
                                    actualTitle: '',
                                    geoSuffix: '',
                                  });
                                  setManualMatchRowId(null);
                                  if (setDocumentMatchRowId) setDocumentMatchRowId(null);
                                  setInvalidMatchState({
                                    open: true,
                                    rowId: row.id,
                                    colName: col,
                                    cellValue: cellValue ?? '',
                                    type: 'TEMPLATE_MISMATCH',
                                    searchTarget: getSearchString(row),
                                  });
                                }}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: isImported ? 'default' : 'pointer',
                                  minHeight: '20px',
                                  minWidth: '50px',
                                  '&:hover .title-text': {
                                    color: isImported ? '#9e9e9e' : '#1976d2',
                                    textDecoration: 'none',
                                  },
                                }}>
                                <Typography
                                  className='title-text'
                                  variant='body2'
                                  sx={{
                                    fontWeight: 500,
                                    color: isImported
                                      ? '#9e9e9e'
                                      : !cellHasData
                                        ? 'error.main'
                                        : '#333',
                                    fontStyle: !cellHasData ? 'italic' : 'normal',
                                    transition: 'color 0.2s',
                                  }}>
                                  {cellHasData
                                    ? makeUppercase && typeof cellValue === 'string'
                                      ? cellValue.toUpperCase()
                                      : cellValue
                                    : 'Enter Title'}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : isPositionCol ? (
                            /* POSITION / LAT LNG COLUMN OVERRIDE */
                            (() => {
                              let hasCoords = false;
                              if (row.lat !== undefined && row.lng !== undefined)
                                hasCoords = true;
                              else if (cellHasData) {
                                const matches = String(cellValue).match(/-?\d+(\.\d+)?/g);
                                if (matches && matches.length >= 2) hasCoords = true;
                              }
                              if (!hasCoords) {
                                const rawLat =
                                  row['Latitude'] ??
                                  row['latitude'] ??
                                  row['Lat'] ??
                                  row['lat'] ??
                                  row['LAT'] ??
                                  row['LATITUDE'];
                                const rawLng =
                                  row['Longitude'] ??
                                  row['longitude'] ??
                                  row['Lng'] ??
                                  row['lng'] ??
                                  row['LNG'] ??
                                  row['LONGITUDE'] ??
                                  row['Lon'] ??
                                  row['lon'];
                                if (rawLat !== undefined && rawLng !== undefined)
                                  hasCoords = true;
                              }

                              const isManualCoords = !!row._manuallyAssigned?.coords;
                              const circleColor = isImported
                                ? '#9e9e9e'
                                : isManualCoords
                                  ? '#00E676'
                                  : '#00B0FF';

                              return (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}>
                                  {hasCoords ? (
                                    <Tooltip
                                      title={
                                        !isImported
                                          ? isDuplicate
                                            ? 'Move this marker to avoid conflict'
                                            : 'Click to modify coordinates for this place'
                                          : ''
                                      }
                                      arrow
                                      placement='top'
                                      slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                      <span>
                                        <IconButton
                                          size='small'
                                          disabled={isImported}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setInvalidMatchState({
                                              open: false,
                                              rowId: null,
                                              colName: null,
                                              cellValue: '',
                                              type: null,
                                              searchTarget: '',
                                            });
                                            setManualMatchRowId(null);
                                            if (setDocumentMatchRowId)
                                              setDocumentMatchRowId(null);
                                            setResolveCoordsState({
                                              open: true,
                                              rowId: row.id,
                                              searchTarget: fallbackTitle,
                                              actualTitle: fallbackTitle,
                                              geoSuffix: getGeoSuffix(row),
                                            });
                                          }}
                                          sx={{ p: 0, '&:hover': { opacity: 0.8 } }}>
                                          {isDuplicate && !isImported ? (
                                            <svg
                                              width='18'
                                              height='18'
                                              viewBox='0 0 24 24'
                                              fill='none'
                                              xmlns='http://www.w3.org/2000/svg'>
                                              <path
                                                d='M12 2L1 21H23L12 2Z'
                                                fill='black'
                                                stroke='black'
                                                strokeWidth='2'
                                                strokeLinejoin='round'
                                              />
                                              <path
                                                d='M12 10V15'
                                                stroke='#FFEA00'
                                                strokeWidth='3'
                                                strokeLinecap='round'
                                              />
                                              <circle
                                                cx='12'
                                                cy='18'
                                                r='1.5'
                                                fill='#FFEA00'
                                              />
                                            </svg>
                                          ) : (
                                            <Box
                                              sx={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                backgroundColor: circleColor,
                                                border: '1px solid rgba(0,0,0,0.15)',
                                              }}
                                            />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip
                                      title={
                                        !isImported
                                          ? 'Find the Lat,Lng for this place?'
                                          : ''
                                      }
                                      arrow
                                      placement='top'
                                      slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                      <span>
                                        <IconButton
                                          size='small'
                                          disabled={isImported}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setInvalidMatchState({
                                              open: false,
                                              rowId: null,
                                              colName: null,
                                              cellValue: '',
                                              type: null,
                                              searchTarget: '',
                                            });
                                            setManualMatchRowId(null);
                                            if (setDocumentMatchRowId)
                                              setDocumentMatchRowId(null);
                                            setResolveCoordsState({
                                              open: true,
                                              rowId: row.id,
                                              searchTarget: fallbackTitle,
                                              actualTitle: fallbackTitle,
                                              geoSuffix: getGeoSuffix(row),
                                            });
                                          }}
                                          sx={{ p: 0, '&:hover': { opacity: 0.8 } }}>
                                          <CancelIcon
                                            sx={{
                                              color: isImported
                                                ? '#9e9e9e'
                                                : 'error.main',
                                              fontSize: 18,
                                            }}
                                          />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </Box>
                              );
                            })()
                          ) : isEffectiveTemplateProperty ? (
                            /* TEMPLATE PROPERTY FIELDS ZONE */
                            isPhotoColumn ? (
                              row.photoMatch === 'MATCH' ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Tooltip
                                    title={
                                      !isImported
                                        ? 'Click to change photo allocation'
                                        : ''
                                    }
                                    arrow
                                    placement='top'
                                    slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                    <span>
                                      <IconButton
                                        size='small'
                                        disabled={isImported}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setResolveCoordsState({
                                            open: false,
                                            rowId: null,
                                            searchTarget: '',
                                            actualTitle: '',
                                            geoSuffix: '',
                                          });
                                          setInvalidMatchState({
                                            open: false,
                                            rowId: null,
                                            colName: null,
                                            cellValue: '',
                                            type: null,
                                            searchTarget: '',
                                          });
                                          if (setDocumentMatchRowId)
                                            setDocumentMatchRowId(null);
                                          setManualMatchRowId(row.id);
                                        }}
                                        sx={{ p: 0, '&:hover': { opacity: 0.7 } }}>
                                        {row.matchedFiles &&
                                        row.matchedFiles.length > 1 ? (
                                          <Box
                                            sx={{
                                              width: 18,
                                              height: 18,
                                              borderRadius: '50%',
                                              backgroundColor: isImported
                                                ? '#9e9e9e'
                                                : isManuallyAssigned
                                                  ? '#00E676'
                                                  : '#00B0FF',
                                              border: '1px solid rgba(0,0,0,0.2)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}>
                                            <Typography
                                              sx={{
                                                color: 'white',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                              }}>
                                              {row.matchedFiles.length}
                                            </Typography>
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              width: 14,
                                              height: 14,
                                              borderRadius: '50%',
                                              backgroundColor: isImported
                                                ? '#9e9e9e'
                                                : isManuallyAssigned
                                                  ? '#00E676'
                                                  : '#00B0FF',
                                              border: '1px solid rgba(0,0,0,0.2)',
                                            }}
                                          />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Tooltip
                                    title='Click for options'
                                    arrow
                                    placement='top'
                                    slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                    <span>
                                      <IconButton
                                        size='small'
                                        disabled={isImported}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setResolveCoordsState({
                                            open: false,
                                            rowId: null,
                                            searchTarget: '',
                                            actualTitle: '',
                                            geoSuffix: '',
                                          });
                                          setInvalidMatchState({
                                            open: false,
                                            rowId: null,
                                            colName: null,
                                            cellValue: '',
                                            type: null,
                                            searchTarget: '',
                                          });
                                          if (setDocumentMatchRowId)
                                            setDocumentMatchRowId(null);
                                          setManualMatchRowId(row.id);
                                        }}
                                        sx={{ p: 0, '&:hover': { opacity: 0.7 } }}>
                                        <CancelIcon
                                          sx={{
                                            color: isImported ? '#9e9e9e' : 'error.main',
                                            fontSize: 18,
                                          }}
                                        />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              )
                            ) : isDocumentCol ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Tooltip
                                  title={!isImported ? 'Click to manage documents' : ''}
                                  arrow
                                  placement='top'
                                  slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                  <span>
                                    <IconButton
                                      size='small'
                                      disabled={isImported}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setResolveCoordsState({
                                          open: false,
                                          rowId: null,
                                          searchTarget: '',
                                          actualTitle: '',
                                          geoSuffix: '',
                                        });
                                        setInvalidMatchState({
                                          open: false,
                                          rowId: null,
                                          colName: null,
                                          cellValue: '',
                                          type: null,
                                          searchTarget: '',
                                        });
                                        setManualMatchRowId(null);
                                        if (setDocumentMatchRowId)
                                          setDocumentMatchRowId(row.id);
                                      }}
                                      sx={{ p: 0, '&:hover': { opacity: 0.7 } }}>
                                      {row.documents && row.documents.length > 0 ? (
                                        <Box
                                          sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            backgroundColor: isImported
                                              ? '#9e9e9e'
                                              : isManuallyAssigned
                                                ? '#00E676'
                                                : '#00B0FF',
                                            border: '1px solid rgba(0,0,0,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}>
                                          <Typography
                                            sx={{
                                              color: 'white',
                                              fontSize: '10px',
                                              fontWeight: 'bold',
                                            }}>
                                            {row.documents.length}
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <CancelIcon
                                          sx={{
                                            color: isImported ? '#9e9e9e' : 'error.main',
                                            fontSize: 18,
                                          }}
                                        />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            ) : isIconCol ? (
                              (() => {
                                let currentIconId = null;
                                if (hasGlobalIcon) {
                                  currentIconId = rowSubMappings[iconColKey];
                                } else if (isManuallyAssigned) {
                                  currentIconId = row[col];
                                } else {
                                  let matched = null;
                                  const cleanCellValue =
                                    cellValue !== undefined && cellValue !== null
                                      ? String(cellValue).trim()
                                      : '';
                                  if (cleanCellValue !== '') {
                                    matched = markerIcons?.find(
                                      (i) =>
                                        normalizeString(i.name) !== '' &&
                                        normalizeString(i.name) ===
                                          normalizeString(cleanCellValue),
                                    );
                                  }
                                  if (matched) {
                                    currentIconId = matched.id;
                                  } else {
                                    const def = markerIcons?.find(
                                      (i) => i.isDefault === true,
                                    );
                                    if (def) currentIconId = def.id;
                                  }
                                }
                                const cIcon = markerIcons?.find(
                                  (i) => i.id === currentIconId,
                                );
                                const cUrl = getIconUrl(cIcon);

                                return (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}>
                                    <Tooltip
                                      title={
                                        !isImported
                                          ? hasGlobalIcon
                                            ? 'Icon mapped globally'
                                            : 'Click to change Icon'
                                          : ''
                                      }
                                      arrow
                                      placement='top'
                                      slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                      <span>
                                        <IconButton
                                          size='small'
                                          disabled={isImported || hasGlobalIcon}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setResolveCoordsState({
                                              open: false,
                                              rowId: null,
                                              searchTarget: '',
                                              actualTitle: '',
                                              geoSuffix: '',
                                            });
                                            setManualMatchRowId(null);
                                            if (setDocumentMatchRowId)
                                              setDocumentMatchRowId(null);
                                            setInvalidMatchState({
                                              open: true,
                                              rowId: row.id,
                                              colName: col,
                                              cellValue: cellValue ?? '',
                                              type: 'ICON',
                                              searchTarget: getSearchString(row),
                                            });
                                          }}
                                          sx={{
                                            p: 0,
                                            '&:hover': { opacity: 0.8 },
                                            flexShrink: 0,
                                          }}>
                                          {cUrl ? (
                                            <img
                                              src={cUrl}
                                              alt='icon'
                                              style={{
                                                width: 18,
                                                height: 18,
                                                objectFit: 'contain',
                                              }}
                                            />
                                          ) : (
                                            <Box
                                              sx={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                backgroundColor:
                                                  isManuallyAssigned && cellHasData
                                                    ? '#00E676'
                                                    : '#00B0FF',
                                                border: '1px solid rgba(0,0,0,0.15)',
                                              }}
                                            />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                );
                              })()
                            ) : isNamedRichText ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}>
                                <Tooltip
                                  title={!isImported ? 'Rich Text Mapping' : ''}
                                  arrow
                                  placement='top'
                                  slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                  <span>
                                    {row._hasRichTextFlag ||
                                    row.hasRichText ||
                                    cellHasData ? (
                                      <Box
                                        sx={{
                                          display: 'inline-block',
                                          width: 14,
                                          height: 14,
                                          borderRadius: '50%',
                                          backgroundColor: isImported
                                            ? '#9e9e9e'
                                            : '#00B0FF',
                                          border: '1px solid rgba(0,0,0,0.15)',
                                          verticalAlign: 'middle',
                                        }}
                                      />
                                    ) : (
                                      <Typography
                                        variant='caption'
                                        color='text.secondary'>
                                        -
                                      </Typography>
                                    )}
                                  </span>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                {isCellLockedIn ? (
                                  <Tooltip
                                    title={
                                      !isImported
                                        ? 'Click to change or manually resolve property mapping assignment'
                                        : ''
                                    }
                                    arrow
                                    placement='top'
                                    slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                    <span>
                                      <IconButton
                                        size='small'
                                        disabled={isImported}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setResolveCoordsState({
                                            open: false,
                                            rowId: null,
                                            searchTarget: '',
                                            actualTitle: '',
                                            geoSuffix: '',
                                          });
                                          setManualMatchRowId(null);
                                          if (setDocumentMatchRowId)
                                            setDocumentMatchRowId(null);
                                          setInvalidMatchState({
                                            open: true,
                                            rowId: row.id,
                                            colName: col,
                                            cellValue: cellValue ?? '',
                                            type: isRegionCol
                                              ? 'REGION'
                                              : isCategoryCol
                                                ? 'CATEGORY'
                                                : isIconCol
                                                  ? 'ICON'
                                                  : isDescriptionCol
                                                    ? 'DESCRIPTION'
                                                    : 'TEMPLATE_MISMATCH',
                                            searchTarget: getSearchString(row),
                                          });
                                        }}
                                        sx={{
                                          p: 0,
                                          '&:hover': { opacity: 0.8 },
                                          flexShrink: 0,
                                        }}>
                                        <Box
                                          sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: '50%',
                                            backgroundColor: isImported
                                              ? '#9e9e9e'
                                              : '#00B0FF',
                                            border: '1px solid rgba(0,0,0,0.15)',
                                            flexShrink: 0,
                                          }}
                                        />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <Tooltip
                                    title={
                                      !isImported
                                        ? 'Click to change or manually resolve property mapping assignment'
                                        : ''
                                    }
                                    arrow
                                    placement='top'
                                    slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                    <span>
                                      <IconButton
                                        size='small'
                                        disabled={isImported}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setResolveCoordsState({
                                            open: false,
                                            rowId: null,
                                            searchTarget: '',
                                            actualTitle: '',
                                            geoSuffix: '',
                                          });
                                          setManualMatchRowId(null);
                                          if (setDocumentMatchRowId)
                                            setDocumentMatchRowId(null);
                                          setInvalidMatchState({
                                            open: true,
                                            rowId: row.id,
                                            colName: col,
                                            cellValue: cellValue ?? '',
                                            type: isRegionCol
                                              ? 'REGION'
                                              : isCategoryCol
                                                ? 'CATEGORY'
                                                : isIconCol
                                                  ? 'ICON'
                                                  : isDescriptionCol
                                                    ? 'DESCRIPTION'
                                                    : 'TEMPLATE_MISMATCH',
                                            searchTarget: getSearchString(row),
                                          });
                                        }}
                                        sx={{
                                          p: 0,
                                          '&:hover': { opacity: 0.8 },
                                          flexShrink: 0,
                                        }}>
                                        {isInvalidMatch ? (
                                          <CancelIcon
                                            sx={{
                                              color: isImported
                                                ? '#9e9e9e'
                                                : 'error.main',
                                              fontSize: 18,
                                            }}
                                          />
                                        ) : (
                                          <Box
                                            sx={{
                                              width: 14,
                                              height: 14,
                                              borderRadius: '50%',
                                              backgroundColor: isImported
                                                ? '#9e9e9e'
                                                : '#00E676',
                                              border: '1px solid rgba(0,0,0,0.2)',
                                            }}
                                          />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            )
                          ) : (
                            /* UNSPECIFIED SPREADSHEET EXTRA PROPERTIES ZONE */
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                              <Tooltip
                                title={
                                  !isImported
                                    ? 'Click to assign text translation rules'
                                    : ''
                                }
                                arrow
                                placement='top'
                                slotProps={{ popper: { sx: { zIndex: 10020 } } }}>
                                <span>
                                  <IconButton
                                    size='small'
                                    disabled={isImported}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setResolveCoordsState({
                                        open: false,
                                        rowId: null,
                                        searchTarget: '',
                                        actualTitle: '',
                                        geoSuffix: '',
                                      });
                                      setManualMatchRowId(null);
                                      if (setDocumentMatchRowId)
                                        setDocumentMatchRowId(null);
                                      setInvalidMatchState({
                                        open: true,
                                        rowId: row.id,
                                        colName: col,
                                        cellValue: cellValue ?? '',
                                        type: 'TEXT',
                                        searchTarget: getSearchString(row),
                                      });
                                    }}
                                    sx={{
                                      p: 0,
                                      '&:hover': { opacity: 0.8 },
                                      flexShrink: 0,
                                    }}>
                                    {isManuallyAssigned && cellHasData ? (
                                      <Box
                                        sx={{
                                          width: 14,
                                          height: 14,
                                          borderRadius: '50%',
                                          backgroundColor: isImported
                                            ? '#9e9e9e'
                                            : '#00E676',
                                          border: '1px solid rgba(0,0,0,0.2)',
                                        }}
                                      />
                                    ) : cellHasData ? (
                                      <RadioButtonUncheckedIcon
                                        sx={{
                                          color: isImported ? '#9e9e9e' : '#757575',
                                          fontSize: 16,
                                        }}
                                      />
                                    ) : (
                                      <Typography
                                        variant='body2'
                                        sx={{ color: 'text.secondary', px: 1 }}>
                                        -
                                      </Typography>
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
}
